import PostHog from 'posthog-react-native';
import Aes from 'react-native-aes-crypto';
import { POSTHOG_API_KEY, POSTHOG_HOST } from '@env';

import { createLogger } from '../../utils/logger';
import { isAllowedEventProperty } from './allowedProperties';
import type { AnalyticsEventName } from './events';

const logger = createLogger('Analytics');

// PostHog project key is safe to ship in the client bundle (write-only).
// Env var acts as an optional override for staging/alternate projects.
const DEFAULT_API_KEY = 'phc_rBgGpiRZP5qkCHbmessjq9tRVohdx9o3pNMvMrcK2HHM';
const DEFAULT_HOST = 'https://us.i.posthog.com';

// Namespace appended before hashing the user ID (SECURITY_AUDIT.md §5.5).
// Not a secret — the bundle is public — but makes trivial cross-referencing
// between PostHog exports and backend user IDs require more than a copy-
// paste. Rotate by changing this constant + re-identifying on next login.
const IDENTIFY_NAMESPACE = 'myfinalkey.analytics.v1';

type Props = Record<string, string | number | boolean | null | undefined>;

/**
 * Filter a property bag against the event-property allowlist.
 *
 * Dev: throws on any unknown key so instrumentation bugs fail loudly
 * during development.
 * Prod: logs a warning and drops unknown keys. Known keys pass through.
 */
function filterProps(props: Props | undefined): Props | undefined {
  if (!props) return props;
  const out: Props = {};
  for (const [key, value] of Object.entries(props)) {
    if (isAllowedEventProperty(key)) {
      out[key] = value;
      continue;
    }
    if (__DEV__) {
      throw new Error(
        `Analytics: property "${key}" is not on the allowlist. ` +
          `Add it to src/services/analytics/allowedProperties.ts after ` +
          `confirming it contains no PII or secrets.`,
      );
    }
    logger.warn(`Dropping non-allowlisted analytics property: ${key}`);
  }
  return out;
}

/**
 * Hash a user-level identifier with the analytics namespace. Stable
 * across sessions for the same user, non-invertible without knowing
 * the underlying ID.
 */
export async function hashAnalyticsIdentifier(id: string): Promise<string> {
  if (!id) throw new Error('hashAnalyticsIdentifier: id is required');
  return Aes.sha256(`${IDENTIFY_NAMESPACE}:${id}`);
}

class AnalyticsService {
  private client: PostHog | null = null;
  private enabled = false;
  private queue: Array<() => void> = [];

  async init() {
    if (this.client) return;

    const key = POSTHOG_API_KEY || DEFAULT_API_KEY;
    if (!key) {
      logger.debug('PostHog disabled — no API key');
      return;
    }

    try {
      this.client = new PostHog(key, {
        host: POSTHOG_HOST || DEFAULT_HOST,
        flushAt: 20,
        flushInterval: 30000,
        captureAppLifecycleEvents: true,
      });
      this.enabled = true;
      logger.debug('PostHog initialized');

      const queued = this.queue;
      this.queue = [];
      queued.forEach(fn => fn());
    } catch (err) {
      logger.error('PostHog init failed (non-fatal):', err);
    }
  }

  private run(fn: () => void) {
    if (this.enabled && this.client) {
      try {
        fn();
      } catch (err) {
        logger.debug('analytics call failed (non-fatal):', err);
      }
    } else {
      this.queue.push(fn);
    }
  }

  track(event: AnalyticsEventName | string, props?: Props) {
    let filtered: Props | undefined;
    try {
      filtered = filterProps(props);
    } catch (err) {
      // filterProps throws in __DEV__ on unknown keys — re-throw so the
      // developer sees it immediately. In prod the catch in run() swallows
      // and we never reach capture anyway.
      throw err;
    }
    this.run(() => this.client!.capture(event, filtered));
  }

  screen(name: string, props?: Props) {
    const filtered = filterProps(props);
    this.run(() => this.client!.screen(name, filtered));
  }

  /**
   * Associate all subsequent events with this user. The `userId` is
   * hashed (SHA-256 with a namespace prefix) before being sent to
   * PostHog so exports cannot be trivially correlated with backend IDs.
   *
   * Callers pass the raw backend ID; the hashing happens here. Traits
   * go through the same allowlist filter as event properties.
   */
  identify(userId: string, traits?: Props) {
    this.run(async () => {
      let hashed: string;
      try {
        hashed = await hashAnalyticsIdentifier(userId);
      } catch (err) {
        logger.error('Failed to hash analytics identifier, skipping identify');
        return;
      }
      const filtered = filterProps(traits);
      this.client!.identify(hashed, filtered);
    });
  }

  reset() {
    this.run(() => this.client!.reset());
  }

  getClient(): PostHog | null {
    return this.client;
  }
}

export const analytics = new AnalyticsService();
